/**
 * Harrow's branching conversation (Phase 3). One real fork, persisted as
 * one-time flags so the choice/result survives reload and import:
 *
 *   "Show the job table" — succeeds only once `roaches_in_tmp` is complete
 *   (Harrow reads the save, not the vibe); records `harrow_verified`.
 *   "Trust me, bro" — always refused; records `harrow_trust_me`.
 *
 * The dialogue COPY lives in npcs.json (flat dialogue record, existing
 * schema); this module only decides which key to show and what to record.
 */
import type { PlayerState } from '../state';
import { hasFlag, isQuestComplete, withFlag } from './engine';
import { TUTORIAL_QUEST_ID } from './eligibility';

export const HARROW_VERIFIED_FLAG = 'harrow_verified';
export const HARROW_TRUST_FLAG = 'harrow_trust_me';

export type HarrowChoice = 'job-table' | 'trust';
export type HarrowResultKey = 'branch_a' | 'branch_b' | 'branch_locked';

export function applyHarrowChoice(
  state: PlayerState,
  choice: HarrowChoice,
): { state: PlayerState; resultKey: HarrowResultKey } {
  if (choice === 'job-table') {
    if (isQuestComplete(state, TUTORIAL_QUEST_ID)) {
      return { state: withFlag(state, HARROW_VERIFIED_FLAG), resultKey: 'branch_a' };
    }
    return { state, resultKey: 'branch_locked' };
  }
  return { state: withFlag(state, HARROW_TRUST_FLAG), resultKey: 'branch_b' };
}

/** The persisted outcome to show on revisit; verified outranks trust-me. */
export function harrowRecordedResult(state: PlayerState): 'branch_a' | 'branch_b' | null {
  if (hasFlag(state, HARROW_VERIFIED_FLAG)) return 'branch_a';
  if (hasFlag(state, HARROW_TRUST_FLAG)) return 'branch_b';
  return null;
}
